import { Capacitor } from '@capacitor/core'

import FileFormats from '../../constants/FileFormats'
import LabelFormatStorage from '../storage/label-format-storage/LabelFormatStorage'

/**
 * Which label file the server should render when the seller has never chosen one.
 *
 * This preserves the behaviour that shipped before the format became selectable:
 * the tablet drives a Bluetooth 4x4 label printer over ZPL, the web dashboard
 * downloads a PDF and prints it through the browser. Changing these values
 * silently changes what every existing installation prints.
 */
export const getDefaultLabelFormat = (): FileFormats =>
  Capacitor.isNativePlatform() ? FileFormats.ZPL : FileFormats.PDF

/**
 * Order matters — the drawer row cycles through this list on each tap.
 */
export const getAvailableLabelFormats = (): FileFormats[] => [FileFormats.ZPL, FileFormats.PDF]

const isSupportedLabelFormat = (value: string | undefined): value is FileFormats =>
  getAvailableLabelFormats().some(format => format === value)

/**
 * Read per print rather than cached once: the seller can change the format from the
 * drawer at any time and both the data handler and the repository that consume this
 * are module-scope singletons that outlive the change. An unrecognised stored value
 * (hand-edited storage, or a format removed in a later release) falls back to the
 * platform default rather than being sent to the server as-is.
 */
export const getLabelFormat = (): FileFormats => {
  const storedFormat = LabelFormatStorage.get()

  return isSupportedLabelFormat(storedFormat) ? storedFormat : getDefaultLabelFormat()
}

export const setLabelFormat = (format: FileFormats): void => LabelFormatStorage.set(format)
